import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { CampInstanceMatrixRow, CampInstanceDefaults, Coach, Court } from '@court-sync/shared';
import { Collapsible, CollapsibleContent, CollapsibleTrigger, Field, FieldLabel, Input } from '@/components/ui';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';

export interface CampInstanceRowOverride {
  weekIndex: number;
  splitId: string | null;
  campDayPrice?: number;
  campFullPrice?: number;
  maxCapacity?: number;
  skillLevel?: CampInstanceDefaults['skillLevel'];
  /** When omitted, uses defaults.coachIds. */
  coachIds?: string[];
  /** When omitted, uses defaults.courtIds. */
  courtIds?: string[];
  description?: string;
  /** When set on override, replaces group default cover; empty string = no cover. */
  imageUrl?: string;
}

interface CampInstanceOverrideTableProps {
  matrix: CampInstanceMatrixRow[];
  defaults: CampInstanceDefaults;
  getVariantLabel: (splitId: string | null) => string;
  currencySymbol: string;
  overrides: CampInstanceRowOverride[];
  onChange: (overrides: CampInstanceRowOverride[]) => void;
  courts: Court[];
  coaches: Coach[];
  defaultLocationId?: string | null;
  defaultCoverDisplayUrl?: string;
  getRowCoverDisplayUrl?: (index: number) => string;
  hasCustomRowCover?: (index: number) => boolean;
  onRowCoverFile?: (index: number, file: File) => void;
  onRowCoverRemove?: (index: number) => void;
  onRowCoverReset?: (index: number) => void;
  uploadingCover?: boolean;
}

function effectiveValue<T>(override: T | undefined, fallback: T): T {
  return override !== undefined ? override : fallback;
}

function rowKey(row: CampInstanceMatrixRow): string {
  return `${row.weekIndex}-${row.splitId ?? 'none'}`;
}

function activeCourtsForLocation(courts: Court[], locationId: string | null | undefined): Court[] {
  const active = courts.filter((c) => c.isActive);
  if (!locationId) return active;
  return active.filter((c) => c.locationId === locationId);
}

function CampInstanceOverrideTableInner({
  matrix,
  defaults,
  getVariantLabel,
  currencySymbol,
  overrides,
  onChange,
  courts,
  coaches,
  defaultLocationId,
  defaultCoverDisplayUrl = '',
  getRowCoverDisplayUrl,
  hasCustomRowCover,
  onRowCoverFile,
  onRowCoverRemove,
  onRowCoverReset,
  uploadingCover = false,
}: CampInstanceOverrideTableProps) {
  const activeCoaches = useMemo(() => coaches.filter((c) => c.isActive), [coaches]);
  const courtsForPicker = useMemo(
    () => activeCourtsForLocation(courts, defaultLocationId),
    [courts, defaultLocationId]
  );

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() =>
    matrix.length > 0 ? new Set([rowKey(matrix[0]!)]) : new Set()
  );

  const getRow = (index: number): CampInstanceRowOverride => {
    const row = matrix[index]!;
    return (
      overrides[index] ?? {
        weekIndex: row.weekIndex,
        splitId: row.splitId,
        description: '',
      }
    );
  };

  const effectiveCoachIds = (override: CampInstanceRowOverride): string[] =>
    override.coachIds ?? defaults.coachIds;

  const effectiveCourtIds = (override: CampInstanceRowOverride): string[] =>
    override.courtIds ?? defaults.courtIds;

  const hasCustomCoachesOrCourts = (override: CampInstanceRowOverride): boolean =>
    override.coachIds !== undefined || override.courtIds !== undefined;

  const updateRow = (index: number, patch: Partial<CampInstanceRowOverride>) => {
    const row = matrix[index]!;
    const existing = getRow(index);
    const next = [...overrides];
    next[index] = { ...existing, ...patch, weekIndex: row.weekIndex, splitId: row.splitId };
    onChange(next);
  };

  const resetPricing = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const row = matrix[index]!;
    const existing = getRow(index);
    const next = [...overrides];
    next[index] = {
      ...existing,
      weekIndex: row.weekIndex,
      splitId: row.splitId,
      campDayPrice: undefined,
      campFullPrice: undefined,
      maxCapacity: undefined,
    };
    onChange(next);
  };

  const toggleCoach = (index: number, coachId: string) => {
    const row = getRow(index);
    const current = effectiveCoachIds(row);
    const coachIds = current.includes(coachId)
      ? current.filter((id) => id !== coachId)
      : [...current, coachId];
    updateRow(index, { coachIds });
  };

  const toggleCourt = (index: number, courtId: string) => {
    const row = getRow(index);
    const current = effectiveCourtIds(row);
    const courtIds = current.includes(courtId)
      ? current.filter((id) => id !== courtId)
      : [...current, courtId];
    updateRow(index, { courtIds });
  };

  const resetCoachesAndCourts = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const row = matrix[index]!;
    const existing = getRow(index);
    const next = [...overrides];
    const { coachIds: _coachIds, courtIds: _courtIds, ...rest } = existing;
    next[index] = { ...rest, weekIndex: row.weekIndex, splitId: row.splitId };
    onChange(next);
  };

  const toggleExpanded = (key: string, open: boolean) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  if (matrix.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No camp instances to configure. Go back and adjust schedule or split settings.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {matrix.map((row, index) => {
        const key = rowKey(row);
        const override = getRow(index);
        const dayPrice = effectiveValue(override.campDayPrice, defaults.campDayPrice);
        const fullPrice = effectiveValue(override.campFullPrice, defaults.campFullPrice);
        const capacity = effectiveValue(override.maxCapacity, defaults.maxCapacity);
        const rowCoachIds = effectiveCoachIds(override);
        const rowCourtIds = effectiveCourtIds(override);
        const hasCustomPricing =
          override.campDayPrice !== undefined ||
          override.campFullPrice !== undefined ||
          override.maxCapacity !== undefined;
        const hasCustomCoachesCourts = hasCustomCoachesOrCourts(override);
        const hasCustomCover = hasCustomRowCover?.(index) ?? false;
        const rowCoverDisplayUrl = getRowCoverDisplayUrl?.(index) ?? defaultCoverDisplayUrl;
        const isOpen = expandedKeys.has(key);
        const capacityLabel = capacity === -1 ? 'Unlimited' : String(capacity);

        return (
          <Collapsible key={key} open={isOpen} onOpenChange={(open) => toggleExpanded(key, open)}>
            <article className="rounded-lg border border-blue-200 bg-white dark:border-blue-800 dark:bg-gray-900">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-start justify-between gap-3 border-b border-blue-100 bg-blue-50/60 px-4 py-3 text-left transition-colors hover:bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 dark:hover:bg-blue-950/50"
                  aria-expanded={isOpen}
                >
                  <div className="flex min-w-0 items-start gap-2">
                    <ChevronDown
                      className={`mt-0.5 h-4 w-4 shrink-0 text-blue-600 transition-transform dark:text-blue-400 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Week {row.weekIndex}
                        {row.splitId ? (
                          <span className="font-normal text-gray-600 dark:text-gray-400">
                            {' '}
                            · {getVariantLabel(row.splitId)}
                          </span>
                        ) : null}
                      </h3>
                      {!isOpen ? (
                        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                          {currencySymbol}
                          {dayPrice} / day · {currencySymbol}
                          {fullPrice} weekly · cap {capacityLabel} · {rowCoachIds.length} coach
                          {rowCoachIds.length !== 1 ? 'es' : ''} · {rowCourtIds.length} court
                          {rowCourtIds.length !== 1 ? 's' : ''}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="space-y-4 p-4">
                  {hasCustomPricing || hasCustomCoachesCourts ? (
                    <div className="flex flex-wrap justify-end gap-3">
                      {hasCustomPricing ? (
                        <button
                          type="button"
                          onClick={(e) => resetPricing(index, e)}
                          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          Reset pricing to defaults
                        </button>
                      ) : null}
                      {hasCustomCoachesCourts ? (
                        <button
                          type="button"
                          onClick={(e) => resetCoachesAndCourts(index, e)}
                          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          Reset coaches & courts to defaults
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Field>
                      <FieldLabel>Day price ({currencySymbol}) *</FieldLabel>
                      <Input
                        type="number"
                        step="0.01"
                        min={0.01}
                        value={dayPrice}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) => {
                          const n = parseFloat(e.target.value);
                          updateRow(index, { campDayPrice: Number.isNaN(n) ? undefined : n });
                        }}
                        aria-label={`Day price for week ${row.weekIndex}`}
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Camp weekly price ({currencySymbol}) *</FieldLabel>
                      <Input
                        type="number"
                        step="0.01"
                        min={0.01}
                        value={fullPrice}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) => {
                          const n = parseFloat(e.target.value);
                          updateRow(index, { campFullPrice: Number.isNaN(n) ? undefined : n });
                        }}
                        aria-label={`Camp weekly price for week ${row.weekIndex}`}
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Capacity *</FieldLabel>
                      {capacity === -1 ? (
                        <p className="text-sm italic text-gray-500">Unlimited (from defaults)</p>
                      ) : (
                        <Input
                          type="number"
                          min={1}
                          value={capacity}
                          onWheel={(e) => e.currentTarget.blur()}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            updateRow(index, { maxCapacity: Number.isNaN(n) ? undefined : n });
                          }}
                          aria-label={`Capacity for week ${row.weekIndex}`}
                        />
                      )}
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Courts * ({rowCourtIds.length} selected)
                      </label>
                      <div className="max-h-32 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
                        {courtsForPicker.length === 0 ? (
                          <p className="text-sm text-gray-500">No active courts available</p>
                        ) : (
                          courtsForPicker.map((court) => (
                            <label
                              key={court.id}
                              className="flex items-center space-x-2 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <input
                                type="checkbox"
                                checked={rowCourtIds.includes(court.id)}
                                onChange={() => toggleCourt(index, court.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{court.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Coaches * ({rowCoachIds.length} selected)
                      </label>
                      <div className="max-h-32 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
                        {activeCoaches.length === 0 ? (
                          <p className="text-sm text-gray-500">No active coaches available</p>
                        ) : (
                          activeCoaches.map((coach) => (
                            <label
                              key={coach.id}
                              className="flex items-center space-x-2 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <input
                                type="checkbox"
                                checked={rowCoachIds.includes(coach.id)}
                                onChange={() => toggleCoach(index, coach.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {coach.firstName} {coach.lastName}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {onRowCoverFile && onRowCoverRemove ? (
                    <div>
                      {hasCustomCover && onRowCoverReset ? (
                        <div className="mb-2 flex justify-end">
                          <button
                            type="button"
                            onClick={() => onRowCoverReset(index)}
                            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          >
                            Reset cover to default
                          </button>
                        </div>
                      ) : null}
                      <LessonCoverImageField
                        displayUrl={rowCoverDisplayUrl}
                        uploading={uploadingCover}
                        inputId={`camp-group-cover-${index}`}
                        onFileSelect={(file) => onRowCoverFile(index, file)}
                        onRemove={() => onRowCoverRemove(index)}
                      />
                    </div>
                  ) : null}

                  <Field>
                    <FieldLabel htmlFor={`description-${index}`}>Description</FieldLabel>
                    <textarea
                      id={`description-${index}`}
                      rows={3}
                      className="input min-h-[4.5rem] w-full resize-y"
                      value={override.description ?? ''}
                      onChange={(e) => updateRow(index, { description: e.target.value })}
                      placeholder={
                        defaults.description?.trim()
                          ? defaults.description.trim()
                          : 'Camp description, what to bring, schedule details...'
                      }
                    />
                    {defaults.description?.trim() ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Leave blank to use the shared default description.
                      </p>
                    ) : null}
                  </Field>
                </div>
              </CollapsibleContent>
            </article>
          </Collapsible>
        );
      })}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Override coaches, courts, description, or cover image for individual camp instances. Location
        and default coaches/courts come from the previous step. Expand a row to edit; pricing and
        cover default from earlier steps.
      </p>
    </div>
  );
}

export function CampInstanceOverrideTable(props: CampInstanceOverrideTableProps) {
  const matrixKey = props.matrix.map(rowKey).join('|');
  return <CampInstanceOverrideTableInner key={matrixKey} {...props} />;
}
